/**
 * Creator Pro Upgrade — the one-time tooling upgrade (formerly "Affiliate").
 *
 * PRODUCT-FIRST by design (Jul 2026, Randy): this card sells the TOOLKIT, not
 * an income opportunity. The Partner Program (referral commission) is a quiet
 * secondary note with the earnings disclaimer attached — never the headline.
 *
 * Three render states:
 *   1. ALREADY PRO       → success badge + "everything's unlocked"
 *   2. NOT-YET-SUBSCRIBED → activate the $X/mo Creator sub first
 *   3. ELIGIBLE          → "Unlock Creator Pro · $X one-time" (tooling outline)
 *
 * Wiring unchanged: POST /api/affiliate/start-checkout → Stripe. On return the
 * dashboard URL carries ?affiliate_session=<id>; AffiliateReturnHandler polls
 * /api/affiliate/checkout-status until the account flips.
 */
import { useEffect, useState } from "react";
import { Crown, Check, Lock, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import CreatorProBuffet from "@/components/CreatorProBuffet";
import PartnerProgramNote from "@/components/PartnerProgramNote";
import ViewWhatYouHaveModal from "@/components/ViewWhatYouHaveModal";
import { useEntitlements } from "@/lib/entitlements";
import { usePublicPricing } from "@/lib/usePublicPricing";

const fmt$ = (cents) => (cents === null || cents === undefined ? "—"
  : `$${((cents || 0) / 100).toFixed(0)}`);

export default function AffiliateUpgrade() {
  const pricing = usePublicPricing();  // Feb 2026 (Randy pt-batch5c) — silo caps + fees live.
  const proSiloCap = pricing.creator_pro_tier_silo_cap || 12;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const { ent } = useEntitlements();

  const ownedKeys = ent?.owned ? Object.keys(ent.owned).filter((k) => ent.owned[k]) : [];
  const ownsEverything = !!ent && (
    ent.all_access ||
    (Object.keys(ent.owned || {}).length > 0 && Object.values(ent.owned).every(Boolean))
  );

  const load = async () => {
    try {
      const r = await api.get("/me/affiliate-status");
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Creator Pro status.", { id: "creator-pro-status-error" });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startCheckout = async ({ selected_skus = null, full_bundle = true } = {}) => {
    setRedirecting(true);
    try {
      const r = await api.post("/affiliate/start-checkout", {
        origin_url: window.location.origin,
        selected_skus,
        full_bundle,
      });
      // Feb 24, 2026 (Randy pt-founder-bypass) — founder short-circuit
      // returns no URL and instead auto-grants Creator Pro. Show a
      // friendly toast + refresh the local status so the "you own
      // Creator Pro" card renders immediately.
      if (r.data?.already_granted) {
        toast.success(r.data.message || "Founder access — Creator Pro auto-granted.");
        setRedirecting(false);
        await load();
        try { window.dispatchEvent(new CustomEvent("profile-updated")); } catch { /* fine */ }
        return;
      }
      if (r.data?.url) window.location.href = r.data.url;
      else throw new Error("No checkout URL returned");
    } catch (e) {
      setRedirecting(false);
      toast.error(e.response?.data?.detail || "Could not start checkout.");
    }
  };

  if (loading) return <div className="text-[#6B6A68] text-sm py-2" data-testid="affiliate-upgrade-loading">Loading…</div>;
  if (!status) return null;

  // STATE 1: already a Creator Pro member who owns EVERYTHING. (A partial
  // à-la-carte member falls through to the buffet below to add the rest.)
  if (status.is_affiliate && ownsEverything) {
    // Iter160o (Randy pt-siloart-seagreen-encased, Feb 2026) — this
    // "Creator Pro" success card is a proper feature card with its
    // own #E8F0E9 sea-green bg, but that color isn't in the CSS
    // exclusion chain (only `bg-white` and `bg-[#F4F8F5]` are).
    // Marking it `.siloart-encased` explicitly exempts its text
    // from the whitetext/blacktext toggle so the h3 + p keep their
    // native dark color like every other card.
    return (
      <div className="bg-[#E8F0E9] border border-[#4A5D4E]/30 rounded-xl p-5 siloart-encased" data-testid="affiliate-upgrade-active">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#4A5D4E] text-white flex items-center justify-center shrink-0">
            <Crown size={20} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif-display text-xl text-[#1C1B1A]">You're a Creator Pro</h3>
              <span className="text-[10px] uppercase tracking-[0.2em] bg-[#4A5D4E] text-white px-2 py-0.5 rounded">
                <Check size={10} weight="bold" className="inline mr-1" />Active
              </span>
            </div>
            <p className="text-sm text-[#6B6A68] mt-1">
              Every Creator Pro tool is unlocked on your account — up to {proSiloCap} silos, Boost, Macros, Collaboration, Silo Wars, the AI Assistant, and the YouTube Digital Twin Kit.
            </p>
            {status.affiliate_activated_at && (
              <p className="text-xs text-[#6B6A68] mt-1">
                Pro since {new Date(status.affiliate_activated_at).toLocaleDateString()}
              </p>
            )}
            {/* Aug 2026 (Randy pt-view-what-you-have) — the "View what you
                have" trigger. Non-transactional; opens a read-only modal
                with an optional Pay-It-Forward section for creators whose
                referrer is still an active affiliate. */}
            <button
              type="button"
              onClick={() => setViewOpen(true)}
              data-testid="affiliate-upgrade-view-what-you-have"
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#4A5D4E] hover:text-[#3A4D3E] underline underline-offset-2 hover:no-underline"
            >
              <Eye size={13} weight="duotone" /> View what you have
            </button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#4A5D4E]/20">
          <p className="text-xs text-[#6B6A68] mb-2">
            <PartnerProgramNote
              testId="affiliate-upgrade-enrolled-note"
              intro="As a Creator Pro member you're also enrolled in the "
            />
          </p>
          <EarningsDisclaimer variant="short" />
        </div>
        <ViewWhatYouHaveModal open={viewOpen} onOpenChange={setViewOpen} />
      </div>
    );
  }

  // STATE 2: needs to activate the paid subscription first.
  if (!status.is_active_subscriber) {
    return (
      <div className="bg-white border hairline rounded-xl p-5" data-testid="affiliate-upgrade-blocked">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#6B6A68] flex items-center justify-center shrink-0">
            <Lock size={20} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="font-serif-display text-xl text-[#1C1B1A]">Creator Pro · Locked</h3>
            <p className="text-sm text-[#6B6A68] mt-1">
              Activate your {fmt$(status.creator_monthly_fee_cents)}/mo Creator subscription first. Creator Pro is a one-time
              upgrade <strong>on top of</strong> the regular subscription — not a replacement.
            </p>
            <p className="text-xs text-[#6B6A68] mt-2">
              Once your subscription is active, this card flips to the upgrade.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // STATE 3: eligible — the product-first Creator Pro buffet.
  return (
    <div className="bg-gradient-to-br from-[#1C1B1A] to-[#3A4A3E] text-[#FDFCF8] rounded-xl p-6 relative overflow-hidden" data-testid="affiliate-upgrade-cta">
      <div className="absolute top-3 right-3 opacity-15"><Crown size={80} weight="duotone" /></div>
      <div className="relative z-10">
        <CreatorProBuffet status={status} owned={ownedKeys} onCheckout={startCheckout} redirecting={redirecting} />
        <div className="mt-5 pt-5 border-t border-white/10">
          <p className="text-[11px] text-[#E5E5E0]/80 mb-1.5">
            <PartnerProgramNote testId="affiliate-upgrade-eligible-note" />
          </p>
          <EarningsDisclaimer variant="short" className="text-[#A8A8A0]" />
        </div>
      </div>
    </div>
  );
}
